<template>
  <div class="cpe-credits">
    <div class="cpe-credits__wrapper">
      <div class="cpe-credits__credits-highlighter">
        <CPECreditsBg class="cpe-credits__credits-highlighter-bg" />
        <div class="cpe-credits__global-count">
          <div class="cpe-credits__global-count-number">
            <DigitScrollCounter :number="cpeGlobalCredits" />
          </div>
          <div class="cpe-credits__global-count-title">INE CPE CREDITS</div>
        </div>
      </div>
      <div class="cpe-credits__global-count--shadow"></div>
      <div class="cpe-credits__description">
        <div class="cpe-credits__description-title">
          Renew any INE certification by submitting
          {{ minimunCreditsForRenewal }} CPE credits
        </div>
        <div class="cpe-credits__description-text">
          Complete INE courses, capture the flag and Skill Check labs to earn
          CPE credits to put towards INE certification renewals.
          <a
            class="cpe-credits__description-text--link"
            :href="linkToCPEProgram"
            target="_blank"
          >
            Learn more
          </a>
          about our continued professional education program.
        </div>
        <div class="cpe-credits__description-bottom-section">
          <div class="cpe-credits__renewals-count">
            <span v-if="cpeGlobalCredits === 0">
              Earn CPE credits to renew your certifications
            </span>
            <span v-else-if="cpeGlobalCredits > minimunCreditsForRenewal">
              You have enough CPE credits to renew:
              {{ certRenewalsAvailableCount }} certification{{
                certRenewalsAvailableCount > 1 ? 's' : ''
              }}
            </span>
            <span v-else>
              You are {{ creditsMissingForRenewal }} CPE credits away from a
              certification renewal!
            </span>
          </div>
          <ModalContainer
            v-if="cpeCreditHistory.length"
            triggerLabel="See history"
            triggerStyles="btn btn--tertiary btn--small cpe-credits__credits-history"
          >
            <CPECreditsHistoryModal />
          </ModalContainer>
        </div>
      </div>
    </div>
    <div class="cpe-credits__non-ine-content">
      If you've completed related non-INE training, you may qualify for
      additional credits.
      <ModalContainer
        :showModal="showCPEExternalModal"
        :showCloseBtn="false"
        customTop="70px"
        class="cpe-credits__non-ine-content-link"
        link-trigger
        triggerLabel="Upload supporting documentation,"
        triggerStyles="cpe-credits__non-ine-content-link-label"
        @open-modal="showCPEExternalModal = true"
      >
        <CPECreditsExternalModal @close-modal="closeCPEExternalModal" />
      </ModalContainer>
      and we will review it for validity.
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DigitScrollCounter from '@/components/user-dashboard/cpe-program/DigitScrollCounter.vue'
import CPECreditsBg from '@/assets/images/cpe-program/cpe-credits-bg.svg'
import ModalContainer from '@/components/shared/ModalContainer.vue'
import CPECreditsHistoryModal from '@/components/user-dashboard/cpe-program/CPECreditsHistoryModal.vue'
import CPECreditsExternalModal from '@/components/user-dashboard/cpe-program/CPECreditsExternalModal.vue'
import CONSTANTS from '@/constants'

const {
  CPE: { LINK_TO_CPE_PROGRAM },
} = CONSTANTS

export default {
  name: 'CPECredits',
  components: {
    DigitScrollCounter,
    CPECreditsBg: { ...CPECreditsBg, name: 'CPECreditsBg' },
    ModalContainer,
    CPECreditsHistoryModal,
    CPECreditsExternalModal,
  },

  data() {
    return {
      minimunCreditsForRenewal: 36,
      showCPEExternalModal: false,
      linkToCPEProgram: LINK_TO_CPE_PROGRAM,
    }
  },
  computed: {
    ...mapGetters({
      cpeGlobalCredits: 'cpeProgram/cpeGlobalCredits',
      cpeCreditHistory: 'cpeProgram/cpeCreditHistory',
    }),
    creditsMissingForRenewal() {
      return this.minimunCreditsForRenewal - this.cpeGlobalCredits
    },
    certRenewalsAvailableCount() {
      return Math.floor(this.cpeGlobalCredits / this.minimunCreditsForRenewal)
    },
  },
  async created() {
    await this.getCPECreditsHistory()
  },
  methods: {
    ...mapActions({
      getCPECreditsHistory: 'cpeProgram/getCPECreditsHistory',
    }),
    closeCPEExternalModal() {
      this.showCPEExternalModal = false
    },
  },
}
</script>

<style lang="scss" scoped>
.cpe-credits {
  $this: &;
  margin-bottom: 30px;

  &__wrapper {
    border-radius: 10px;
    display: flex;
    flex-flow: column;
    justify-content: space-evenly;
    align-items: center;
    margin-bottom: 10px;
    background-image: var(--purple-gradient-two-vertical);
    background-repeat: no-repeat;
    background-size: contain;
    background-position: 100% 100%;
    position: relative;
    overflow: hidden;
    @include mobile {
      background-image: var(--purple-gradient-two);
      flex-flow: row;
    }
  }
  &__credits-highlighter {
    position: absolute;
    height: 100%;
    @include mobile {
      width: 100%;
    }
    &-bg {
      @include mobile {
        position: absolute;
        left: -15px;
        height: 100%;
      }
    }
  }

  &__global-count {
    display: flex;
    flex-flow: column;
    align-items: center;
    margin: 60px 50px;

    position: absolute;
    top: 0;
    left: 10px;
    width: 115px;

    @include mobile {
      position: unset;
      top: unset;
      left: unset;
      margin: 50px;
      width: fit-content;
    }
    @include desktop {
      margin: 70px 50px;
    }
    &-number {
      color: var(--white);
      font-size: 66px;
      font-weight: bold;
      text-align: center;
      line-height: 70px;
    }
    &-title {
      color: var(--positive-light);
      text-align: center;
      font-size: rem(12px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(14px);
      letter-spacing: 1px;
    }
    &--shadow {
      flex-basis: 200px;
      @include mobile {
        flex-basis: 375px;
      }
    }
  }

  &__description {
    padding: 30px;
    display: flex;
    flex-flow: column;
    gap: 10px;
    z-index: 1;
    margin-left: 60px;
    &-title {
      color: var(--white);
      font-size: rem(20px);
      font-style: normal;
      font-weight: 700;
      line-height: rem(26px);
    }

    &-text {
      color: var(--light-blue-gray);
      font-size: rem(14px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px);
      &--link {
        cursor: pointer;
        color: var(--purple-pale);
        font-size: rem(14px);
        font-style: normal;
        font-weight: 450;
        line-height: rem(20px);
      }
    }
    &-bottom-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-height: 36px;
    }
  }

  &__renewals-count {
    color: var(--positive-light);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(20px);
  }

  &__non-ine-content {
    color: var(--light-blue-gray);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(20px);
    &-link {
      display: inline-flex;
    }
  }
}
</style>
<style lang="scss">
.cpe-credits__non-ine-content-link-label {
  cursor: pointer;
  color: var(--purple-pale);
  font-size: rem(14px);
  font-style: normal;
  font-weight: 450;
  line-height: rem(20px);
}
.cpe-credits__credits-highlighter-bg {
  mix-blend-mode: screen;
}
</style>
